import React, { useEffect, useRef } from 'react';
import { Pressable, Platform, TouchableOpacity } from 'react-native';
import { View, Text } from '@constants/Themed';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import moment from 'moment';
import { XStack, YStack } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import globalStyles from '@stylesheet';

interface TimelineEntry {
  id: string;
  date: string;
  title?: string;
  description?: string;
  status?: 'completed' | 'pending' | 'failed';
  data?: any; // For legacy data compatibility
}

interface TimeLineItemProps {
  item: TimelineEntry;
  index: number;
  isActive: boolean;
  onPress: () => void;
  vertical?: boolean;
  width?: number;
  height?: number;
}

const TimeLineItem: React.FC<TimeLineItemProps> = ({
  item,
  index,
  isActive,
  onPress,
  vertical = false,
  width = 80,
  height = 80
}) => {
  const theme = themeSelector();
  const animRef = useRef<any>(null);


  // Check for legacy data format
  const legacyData = item.data;
  const hasLegacyData = legacyData && typeof legacyData === 'object';
  
  // Count legacy data items
  const inboxCount = hasLegacyData && legacyData.mail 
    ? legacyData.mail.filter((mail: any) => mail?.labelIds?.includes("INBOX"))?.length || 0
    : 0;
  const sentCount = hasLegacyData && legacyData.mail 
    ? legacyData.mail.filter((mail: any) => mail?.labelIds?.includes("SENT"))?.length || 0
    : 0;
  const phoneCount = hasLegacyData ? legacyData.phone?.length || 0 : 0;
  const messagesCount = hasLegacyData ? legacyData.messages?.length || 0 : 0;
  const calendarCount = hasLegacyData ? legacyData.calendar?.length || 0 : 0;
  const notesCount = hasLegacyData ? legacyData.notes?.length || 0 : 0;

  const getStatusColor = () => {
    switch (item.status) {
      case 'completed':
        return '#10b981'; // green
      case 'failed':
        return '#ef4444'; // red
      case 'pending':
      default:
        return '#f59e0b'; // amber
    }
  };

  const getStatusIcon = () => {
    if (hasLegacyData) {
      // For legacy data, show mail icon if there's mail data
      if (inboxCount > 0 || sentCount > 0) return 'mail';
      if (phoneCount > 0) return 'phone';
      if (messagesCount > 0) return 'message';
      if (calendarCount > 0) return 'event';
      if (notesCount > 0) return 'note';
    }
    
    // Default status icons
    switch (item.status) {
      case 'completed':
        return 'check-circle';
      case 'failed':
        return 'error';
      case 'pending':
      default:
        return 'schedule';
    }
  };

  const formatDate = (dateString: string) => {
    const date = moment(dateString);
    const today = moment();
    
    if (date.isSame(today, 'day')) {
      return 'Today';
    } else if (date.isSame(today.clone().subtract(1, 'day'), 'day')) {
      return 'Yesterday';
    } else if (date.isSame(today.clone().add(1, 'day'), 'day')) {
      return 'Tomorrow';
    } else {
      return date.format('MMM DD');
    }
  };

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.8}
      style={{
        width: vertical ? '100%' : width,
        height: vertical ? height : '100%'
      }}
    >
      <Animatable.View
        ref={animRef}
        useNativeDriver
        animation={isActive ? 'pulse' : undefined}
        style={Platform.OS === 'web'
          ? {
            ...globalStyles.modernCard,
            ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark),
            width: vertical ? '100%' : width,
            minHeight: height,
            padding: 16,
            borderWidth: isActive ? 2 : 0,
            borderColor: isActive ? getStatusColor() : 'transparent',
            transform: [{ scale: isActive ? 1.02 : 1 }]
          }
          : [
            globalStyles.modernCard,
            theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
            {
              width: vertical ? '100%' : width,
              minHeight: height,
              padding: 16,
              borderWidth: isActive ? 2 : 0,
              borderColor: isActive ? getStatusColor() : 'transparent',
              transform: [{ scale: isActive ? 1.02 : 1 }]
            }
          ]
        }
      >
        {/* Status Badge */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingHorizontal: 10,
          paddingVertical: 6,
          backgroundColor: `${getStatusColor()}20`,
          borderRadius: 8,
          marginBottom: 12,
          alignSelf: 'flex-start'
        }}>
          <MaterialIcons name={getStatusIcon()} size={14} color={getStatusColor()} />
          <Text style={{
            color: getStatusColor(),
            fontSize: 11,
            fontWeight: '600',
            textTransform: 'capitalize'
          }}>
            {item.status || 'Pending'}
          </Text>
        </View>

        {/* Date Info */}
        <View style={{ marginBottom: 12 }}>
          <Text style={{
            color: theme ? "#1e293b" : "#f1f5f9",
            fontSize: 18,
            fontWeight: '700',
            marginBottom: 4
          }}>
            {formatDate(item.date)}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <MaterialIcons name="calendar-today" size={14} color={theme ? '#64748b' : '#94a3b8'} />
            <Text style={{
              color: theme ? "#64748b" : "#94a3b8",
              fontSize: 12
            }}>
              {moment(item.date).format('MMMM DD, YYYY')}
            </Text>
          </View>
        </View>

        {/* Title */}
        {item.title && (
          <Text style={{
            color: theme ? "#1e293b" : "#f1f5f9",
            fontSize: 14,
            fontWeight: '500',
            marginBottom: 12
          }}
            numberOfLines={2}
          >
            {item.title}
          </Text>
        )}

        {/* Description */}
        {item.description && (
          <Text style={{
            color: theme ? "#64748b" : "#94a3b8",
            fontSize: 12,
            lineHeight: 18,
            marginBottom: 12
          }}
            numberOfLines={3}
          >
            {item.description}
          </Text>
        )}

        {/* Legacy Data Indicators */}
        {hasLegacyData && (
          <View style={{
            flexDirection: 'row',
            gap: 8,
            marginTop: 8,
            paddingTop: 12,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e2e8f0' : '#334155'
          }}>
            {(inboxCount > 0 || sentCount > 0) && (
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                paddingHorizontal: 8,
                paddingVertical: 4,
                backgroundColor: theme ? '#dcfce7' : '#14532d',
                borderRadius: 6
              }}>
                <MaterialIcons name="mail" size={12} color={theme ? '#16a34a' : '#86efac'} />
                <Text style={{
                  color: theme ? '#16a34a' : '#86efac',
                  fontSize: 10,
                  fontWeight: '600'
                }}>
                  {inboxCount + sentCount}
                </Text>
              </View>
            )}
            {phoneCount > 0 && (
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                paddingHorizontal: 8,
                paddingVertical: 4,
                backgroundColor: theme ? '#dbeafe' : '#1e3a8a',
                borderRadius: 6
              }}>
                <MaterialIcons name="phone" size={12} color={theme ? '#2563eb' : '#93c5fd'} />
                <Text style={{
                  color: theme ? '#2563eb' : '#93c5fd',
                  fontSize: 10,
                  fontWeight: '600'
                }}>
                  {phoneCount}
                </Text>
              </View>
            )}
            {messagesCount > 0 && (
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                paddingHorizontal: 8,
                paddingVertical: 4,
                backgroundColor: theme ? '#fef3c7' : '#78350f',
                borderRadius: 6
              }}>
                <MaterialIcons name="message" size={12} color={theme ? '#d97706' : '#fbbf24'} />
                <Text style={{
                  color: theme ? '#d97706' : '#fbbf24',
                  fontSize: 10,
                  fontWeight: '600'
                }}>
                  {messagesCount}
                </Text>
              </View>
            )}
          </View>
        )}
      </Animatable.View>
    </TouchableOpacity>
  );
};

export default TimeLineItem;

// Export types for backwards compatibility
export type { TimelineEntry, TimeLineItemProps };
